import { Globe, Code, Rocket, Eye, Trash, Link } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export const WebsiteDetails = ({
  selectedWebsite, deploying, onDeploy, onDelete,
}) => {
  if (!selectedWebsite) {
    return (
      <div className="flex items-center justify-center h-full text-center">
        <div>
          <Globe size={64} className="mx-auto mb-4 text-muted-foreground opacity-30" />
          <p className="text-muted-foreground">Select a website to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-2xl font-bold mb-2">{selectedWebsite.name}</h3>
          <p className="text-sm text-muted-foreground mb-3">{selectedWebsite.description}</p>
          {selectedWebsite.deployed && selectedWebsite.netlify_url && (
            <a
              href={selectedWebsite.netlify_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-green-500 hover:underline"
            >
              <Link size={16} />
              <span className="font-medium">{selectedWebsite.netlify_url}</span>
            </a>
          )}
        </div>
        <Button
          onClick={() => onDelete(selectedWebsite.id)}
          variant="ghost"
          size="sm"
          className="hover:bg-destructive hover:text-white"
          data-testid="delete-website-button"
        >
          <Trash size={18} />
        </Button>
      </div>

      {/* Deployment Status */}
      {!selectedWebsite.deployed && (
        <div className="mb-6 p-6 border-2 border-dashed border-yellow-500/50 bg-yellow-500/5">
          <div className="flex items-center gap-3 mb-3">
            <Rocket size={32} className="text-yellow-500" />
            <div>
              <h4 className="font-semibold">Ready to Deploy</h4>
              <p className="text-sm text-muted-foreground">
                Website generated! Deploy to Netlify for free hosting.
              </p>
            </div>
          </div>
          <Button
            onClick={() => onDeploy(selectedWebsite.id)}
            disabled={deploying}
            className="w-full bg-green-600 text-white hover:bg-green-700"
            data-testid="deploy-button"
          >
            {deploying ? "Deploying..." : "Deploy to Netlify (Free)"}
          </Button>
        </div>
      )}

      {/* Files */}
      <div className="border border-border p-4 bg-surface/50">
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <Code size={20} />
          Generated Files ({selectedWebsite.files?.length || 0})
        </h4>
        <div className="space-y-3">
          {selectedWebsite.files?.map((file, idx) => (
            <div key={idx} className="border border-border/50 bg-black/30">
              <div className="p-3 bg-surface/50 border-b border-border/50 flex items-center justify-between">
                <span className="text-sm font-medium font-mono">{file.name}</span>
                <Button variant="ghost" size="sm" className="hover:bg-accent">
                  <Eye size={16} />
                </Button>
              </div>
              <pre className="p-3 text-xs overflow-x-auto max-h-60 font-mono">
                {file.content.substring(0, 500)}...
              </pre>
            </div>
          ))}
        </div>
      </div>

      {/* Netlify Info */}
      <div className="mt-6 p-4 border border-border bg-surface/30">
        <h4 className="text-sm font-semibold mb-2">About Netlify Deployment</h4>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>Free hosting with SSL certificate</li>
          <li>Automatic HTTPS and CDN</li>
          <li>Custom domain support</li>
          <li>Instant global deployment</li>
          <li>100GB bandwidth per month free</li>
        </ul>
      </div>
    </div>
  );
};
